export type AudioDebugEvent = {
  ts: number;
  name:
    | 'window_pageshow'
    | 'window_pagehide'
    | 'window_visibilitychange'
    | 'window_freeze'
    | 'window_error'
    | 'window_unhandledrejection'
    | 'last_failure_snapshot_saved'
    | 'recording_start_requested'
    | 'recording_start_succeeded'
    | 'recording_start_failed'
    | 'recording_cleanup'
    | 'mime_selected'
    | 'mediarecorder_started'
    | 'mediarecorder_dataavailable'
    | 'mediarecorder_requestdata'
    | 'mediarecorder_stop_event'
    | 'mediarecorder_error'
    | 'mediarecorder_state'
    | 'recording_stop_requested'
    | 'recording_stop_ignored'
    | 'recording_stop_timeout'
    | 'recording_stop_succeeded'
    | 'recording_stop_failed'
    | 'voice_check_result'
    | 'prompt_audio_play_requested'
    | 'prompt_audio_play_succeeded'
    | 'prompt_audio_play_failed'
    | 'prompt_audio_ended'
    | 'prompt_audio_stopped'
    | 'voiceapi_transcribe_requested'
    | 'voiceapi_reencode_needed'
    | 'voiceapi_reencode_mp3_attempt'
    | 'voiceapi_reencode_mp3_result'
    | 'voiceapi_reencode_failed'
    | 'voiceapi_transcribe_failed_413'
    | 'auto_start_skipped';
  data?: Record<string, unknown>;
};

const MAX_EVENTS = 400;
const STORAGE_KEY = 'AOT_AUDIO_DEBUG_EVENTS_V1';
const CONSOLE_FLAG_KEY = 'AOT_AUDIO_DEBUG_CONSOLE_V1';
const LAST_FAILURE_SNAPSHOT_KEY = 'AOT_AUDIO_LAST_FAILURE_SNAPSHOT_V1';
const MAX_SNAPSHOT_EVENTS = 120;

declare global {
  interface Window {
    __AOT_AUDIO_DEBUG_EVENTS__?: AudioDebugEvent[];
    __AOT_AUDIO_DEBUG_CONSOLE__?: boolean;
    __AOT_AUDIO_DEBUG_SENTINELS__?: boolean;
    AOTAudioDebug?: {
      getEvents: () => AudioDebugEvent[];
      clear: () => void;
      toJSON: () => string;
      summary: () => {
        totalEvents: number;
        firstTs?: number;
        lastTs?: number;
        durationMs?: number;
        countsByName: Record<string, number>;
        lastEvent?: AudioDebugEvent;
      };
      enableConsole: () => void;
      disableConsole: () => void;
    };
  }
}

export type AudioLastFailureSnapshotV1 = {
  v: 1;
  createdAtMs: number;
  createdAtMonotonicMs?: number;
  reason: string;
  href?: string;
  userAgent?: string;
  stage?: string;
  timers?: {
    timeRemainingSec?: number;
    sinceStartMs?: number;
  };
  refs?: Record<string, unknown>;
  audioDebugTail?: AudioDebugEvent[];
};

export function getLastFailureSnapshot(): AudioLastFailureSnapshotV1 | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage?.getItem(LAST_FAILURE_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const snapshot = parsed as Partial<AudioLastFailureSnapshotV1>;
    if (snapshot.v !== 1) return null;
    if (typeof snapshot.createdAtMs !== 'number') return null;
    if (typeof snapshot.reason !== 'string') return null;
    return snapshot as AudioLastFailureSnapshotV1;
  } catch {
    return null;
  }
}

export function clearLastFailureSnapshot(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage?.removeItem(LAST_FAILURE_SNAPSHOT_KEY);
  } catch {
    // ignore
  }
}

export function persistLastFailureSnapshot(input: {
  reason: string;
  stage?: string;
  timers?: {
    timeRemainingSec?: number;
    sinceStartMs?: number;
  };
  refs?: Record<string, unknown>;
}): void {
  if (typeof window === 'undefined') {
    return;
  }

  const now = Date.now();
  let monotonic: number | undefined;
  try {
    monotonic = typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : undefined;
  } catch {
    monotonic = undefined;
  }

  const snapshot: AudioLastFailureSnapshotV1 = {
    v: 1,
    createdAtMs: now,
    createdAtMonotonicMs: monotonic,
    reason: input.reason,
    stage: input.stage,
    timers: input.timers,
    refs: input.refs,
    href: (() => {
      try {
        return window.location.href;
      } catch {
        return undefined;
      }
    })(),
    userAgent: (() => {
      try {
        return navigator.userAgent;
      } catch {
        return undefined;
      }
    })(),
    audioDebugTail: getAudioDebugEvents().slice(-MAX_SNAPSHOT_EVENTS),
  };

  try {
    window.localStorage?.setItem(LAST_FAILURE_SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    // ignore
  }

  // Also log that we persisted a snapshot (useful when inspecting tail events).
  try {
    pushAudioDebugEvent({
      name: 'last_failure_snapshot_saved',
      data: { reason: input.reason, stage: input.stage },
    });
  } catch {
    // ignore
  }
}

function shouldEnableConsoleByDefault(): boolean {
  try {
    if (import.meta.env.DEV) {
      return true;
    }

    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('debugAudio') === '1') {
        return true;
      }

      try {
        if (window.localStorage?.getItem(CONSOLE_FLAG_KEY) === '1') {
          return true;
        }
      } catch {
        // ignore
      }
    }
  } catch {
    // ignore
  }

  return false;
}

function loadPersistedEvents(): AudioDebugEvent[] | null {
  try {
    const raw = window.sessionStorage?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    const events = parsed
      .filter((e): e is AudioDebugEvent =>
        Boolean(e) &&
        typeof (e as AudioDebugEvent).ts === 'number' &&
        typeof (e as AudioDebugEvent).name === 'string'
      )
      .slice(-MAX_EVENTS);
    return events;
  } catch {
    return null;
  }
}

function persistEvents(events: AudioDebugEvent[]): void {
  try {
    window.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(events.slice(-MAX_EVENTS)));
  } catch {
    // ignore
  }
}

function initAudioDebugGlobals(): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (window.AOTAudioDebug) {
    return;
  }

  window.AOTAudioDebug = {
    getEvents: () => getAudioDebugEvents(),
    clear: () => {
      window.__AOT_AUDIO_DEBUG_EVENTS__ = [];
      try {
        window.sessionStorage?.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
    },
    toJSON: () => JSON.stringify(getAudioDebugEvents(), null, 2),
    summary: () => getAudioDebugSummary(),
    enableConsole: () => {
      window.__AOT_AUDIO_DEBUG_CONSOLE__ = true;
      try {
        window.localStorage?.setItem(CONSOLE_FLAG_KEY, '1');
      } catch {
        // ignore
      }
    },
    disableConsole: () => {
      window.__AOT_AUDIO_DEBUG_CONSOLE__ = false;
      try {
        window.localStorage?.removeItem(CONSOLE_FLAG_KEY);
      } catch {
        // ignore
      }
    },
  };
}

function initReloadCrashSentinels(): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (window.__AOT_AUDIO_DEBUG_SENTINELS__) {
    return;
  }
  window.__AOT_AUDIO_DEBUG_SENTINELS__ = true;

  try {
    window.addEventListener('pageshow', (event) => {
      pushAudioDebugEvent({
        name: 'window_pageshow',
        data: {
          persisted: Boolean((event as PageTransitionEvent | undefined)?.persisted),
          visibilityState: document.visibilityState,
        },
      });
    });

    window.addEventListener('pagehide', (event) => {
      pushAudioDebugEvent({
        name: 'window_pagehide',
        data: {
          persisted: Boolean((event as PageTransitionEvent | undefined)?.persisted),
          visibilityState: document.visibilityState,
        },
      });
    });

    document.addEventListener('visibilitychange', () => {
      pushAudioDebugEvent({
        name: 'window_visibilitychange',
        data: { visibilityState: document.visibilityState },
      });
    });

    // Safari iOS supports 'freeze' on document in some builds.
    document.addEventListener('freeze', () => {
      pushAudioDebugEvent({ name: 'window_freeze', data: { visibilityState: document.visibilityState } });
    });

    window.addEventListener('error', (event) => {
      pushAudioDebugEvent({
        name: 'window_error',
        data: {
          message: (event as ErrorEvent | undefined)?.message,
          filename: (event as ErrorEvent | undefined)?.filename,
          lineno: (event as ErrorEvent | undefined)?.lineno,
          colno: (event as ErrorEvent | undefined)?.colno,
        },
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      let reason: unknown = undefined;
      try {
        reason = (event as PromiseRejectionEvent | undefined)?.reason;
      } catch {
        reason = undefined;
      }
      pushAudioDebugEvent({
        name: 'window_unhandledrejection',
        data: {
          reasonType: typeof reason,
          reason: reason instanceof Error ? reason.message : String(reason),
        },
      });
    });
  } catch {
    // ignore
  }
}

export function initAudioDebug(): void {
  if (typeof window === 'undefined') {
    return;
  }
  initAudioDebugGlobals();

  // Restore persisted events (useful when iOS Safari reloads the tab).
  const persisted = loadPersistedEvents();
  if (persisted && (!window.__AOT_AUDIO_DEBUG_EVENTS__ || window.__AOT_AUDIO_DEBUG_EVENTS__.length === 0)) {
    window.__AOT_AUDIO_DEBUG_EVENTS__ = persisted;
  } else {
    window.__AOT_AUDIO_DEBUG_EVENTS__ ??= [];
  }

  // Make debug visible without manual console commands:
  // - Dev: on by default
  // - Prod: opt-in via ?debugAudio=1 or localStorage flag
  if (window.__AOT_AUDIO_DEBUG_CONSOLE__ === undefined) {
    window.__AOT_AUDIO_DEBUG_CONSOLE__ = shouldEnableConsoleByDefault();
  }

  // Log reload/crash lifecycle signals into the same ring buffer.
  initReloadCrashSentinels();
}

export function pushAudioDebugEvent(event: Omit<AudioDebugEvent, 'ts'>): void {
  const fullEvent: AudioDebugEvent = { ts: Date.now(), ...event };
  if (typeof window === 'undefined') {
    return;
  }

  initAudioDebugGlobals();

  const list = (window.__AOT_AUDIO_DEBUG_EVENTS__ ??= []);
  list.push(fullEvent);
  if (list.length > MAX_EVENTS) {
    list.splice(0, list.length - MAX_EVENTS);
  }

  // Persist periodically so we can inspect after reloads.
  const shouldPersistImmediately =
    fullEvent.name.startsWith('window_') ||
    fullEvent.name === 'last_failure_snapshot_saved' ||
    fullEvent.name === 'recording_cleanup' ||
    fullEvent.name === 'recording_start_failed' ||
    fullEvent.name === 'recording_stop_failed' ||
    fullEvent.name === 'mediarecorder_error';

  if (shouldPersistImmediately || list.length % 5 === 0) {
    persistEvents(list);
  }

  // Optional, dev-only console mirroring.
  // Enable via: AOTAudioDebug.enableConsole()
  try {
    const isDev = Boolean(import.meta.env.DEV);
    if (isDev && window.__AOT_AUDIO_DEBUG_CONSOLE__) {
      // Keep it compact so iOS Safari doesn't choke.
      // eslint-disable-next-line no-console
      const dataString = fullEvent.data ? JSON.stringify(fullEvent.data) : '';
      console.debug('[AOTAudioDebug]', fullEvent.name, dataString);
    }
  } catch {
    // ignore
  }
}

export function getAudioDebugEvents(): AudioDebugEvent[] {
  if (typeof window === 'undefined') {
    return [];
  }
  return window.__AOT_AUDIO_DEBUG_EVENTS__ ?? [];
}

export function getAudioDebugSummary(): {
  totalEvents: number;
  firstTs?: number;
  lastTs?: number;
  durationMs?: number;
  countsByName: Record<string, number>;
  lastEvent?: AudioDebugEvent;
} {
  const events = getAudioDebugEvents();
  const countsByName: Record<string, number> = {};
  for (const event of events) {
    countsByName[event.name] = (countsByName[event.name] ?? 0) + 1;
  }

  const firstTs = events[0]?.ts;
  const lastTs = events.at(-1)?.ts;
  const durationMs = firstTs && lastTs ? lastTs - firstTs : undefined;

  return {
    totalEvents: events.length,
    firstTs,
    lastTs,
    durationMs,
    countsByName,
    lastEvent: events.at(-1),
  };
}
